import { Injectable } from '@angular/core';
import { EntityCommunicationService, FiltersConfig, IFilterQueryResponse } from '@dataclouder/ngx-core';
import { ILesson, ILessonTaken, LessonPrompts } from '../components/lesson-mini-components/components/lessons.clases';

@Injectable({
  providedIn: 'root',
})
export class DefaultLessonsService extends EntityCommunicationService<ILesson> {
  constructor() {
    super('lesson');
  }

  // --- Endpoint Definitions for methods not in EntityCommunicationService ---
  private endpoints = {
    saveTakenLesson: `api/user/saveLesson`,
    generateLesson: `api/lesson/generate`,
    generateByAI: `api/lesson/generate-ai`,
    improveMDWithAI: `api/lesson/improve-markdown-ai`,
    GenerateBanner: 'api/lesson/generate-banner',
  };

  // --- Method Implementations from LessonsAbstractService ---

  async getLessons(filters: FiltersConfig = {}): Promise<IFilterQueryResponse<ILesson>> {
    if (!filters.returnProps) {
      filters.returnProps = {
        level: 1,
        title: 1,
        description: 1,
        createdDate: 1,
        banner: 1,
        metadata: 1,
        appExtensions: 1,
        media: 1,
      };
    }
    return this.query(filters);
  }

  async getLesson(id: string): Promise<ILesson> {
    return this.findOne(id);
  }

  async postLesson(lesson: ILesson): Promise<ILesson> {
    return this.createOrUpdate(lesson);
  }

  async updateLesson(lesson: ILesson): Promise<ILesson> {
    if (!lesson._id) {
      throw new Error('Lesson ID is required for update.');
    }
    return this.createOrUpdate(lesson);
  }

  async deleteLesson(id: string): Promise<any> {
    await this.remove(id);
  }

  // --- Methods specific to lessons ---

  saveTakenLesson(lesson: ILessonTaken): Promise<any> {
    return this.httpService.post(this.endpoints.saveTakenLesson, lesson);
  }

  async generateLesson(lesson: ILesson): Promise<ILesson> {
    return this.httpService.post<ILesson>(this.endpoints.generateLesson, lesson);
  }

  async postGenerateByAI(id: string): Promise<ILesson> {
    return this.httpService.post<ILesson>(this.endpoints.generateByAI, { id });
  }

  async postImproveMDWithAI(lessonId: string, markdownText: string): Promise<ILesson> {
    return this.httpService.post<ILesson>(this.endpoints.improveMDWithAI, { id: lessonId, markdown: markdownText });
  }

  public generateBanner(prompt: string, lessonId: string): Promise<IFilterQueryResponse<ILesson>> {
    return this.httpService.post<IFilterQueryResponse<ILesson>>(this.endpoints.GenerateBanner, { prompt, lessonId });
  }

  extractTextFromHtml(html: string): string {
    const r1 = new RegExp('~(.+?)~', 'g');
    const lessonHtml = html.replace(r1, (_matching, jsonCoded) => {
      try {
        const data = JSON.parse(jsonCoded);
        return `<span>${data?.settings?.text || ''}</span>`;
      } catch (e) {
        console.error('Error parsing JSON in extractTextFromHtml:', jsonCoded, e);
        return '';
      }
    });
    let text = lessonHtml.replace(/<[^>]*>/g, ' ');
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');
    text = text.replace(/&nbsp;/g, ' ');
    text = text.replace(/&/g, '&');
    text = text.replace(/</g, '<');
    text = text.replace(/>/g, '>');
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  }

  getPrompts(): LessonPrompts {
    return null;
  }
}
